import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { PublicationsDatasourceService } from './publications-datasource.service';
import {
  Publication,
  PublicationSearchFilter,
  PublicationTagResponse,
  PublicationCategoryResponse,
} from '../types/publications.types';
import { HttpResponseStatuses } from '@shared/enums/http-statuses';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PageSize } from '../constants/publications-pagination-sizes';
import { PublicationSearchResponse } from '@shared/types/types';
import { QueryParamsUrl } from '@shared/classes/query-params-url';

@Injectable()
export class PublicationsRepositoryService
  extends QueryParamsUrl
  implements OnDestroy
{
  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private dataSource: PublicationsDatasourceService
  ) {
    super(router, route);
    this.handlerParamsUrl();
    this.checkActiveFilters();
    this.loadData();
  }

  private _filters$ = new BehaviorSubject<PublicationSearchFilter | null>(null);
  filters$ = this._filters$.pipe(
    filter(
      (
        filters: PublicationSearchFilter | null
      ): filters is PublicationSearchFilter => !!filters
    )
  );

  categories$ = new BehaviorSubject<PublicationCategoryResponse[]>([]);
  tags$ = new BehaviorSubject<PublicationTagResponse[]>([]);
  publications$ = new BehaviorSubject<Publication[] | null>(null);
  count$ = new BehaviorSubject<number | undefined>(undefined);

  hasActiveFilters$ = new BehaviorSubject<boolean>(false);
  isLoading$ = new BehaviorSubject<boolean>(false);

  private handlerParamsUrl(): void {
    this.queryParams$
      .pipe(
        tap((params: Params) => this.setFiltersFromUrl(params)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private setFiltersFromUrl(params: Params): void {
    this._filters$.next(this.convertToFilters(params));
  }

  private convertToFilters(params: Params): PublicationSearchFilter {
    const { tags, categories, publisher, content, fromdate, todate, page } =
      params;

    return {
      publicationTagIds: tags ? tags.split(',') : null,
      publicationCategoriesIds: categories ? categories.split(',') : null,
      publisherNameQuery: publisher ?? null,
      contentQuery: content ?? null,
      fromDate: fromdate ?? null,
      toDate: todate ?? null,
      page: page ? Number(page) : 1,
    };
  }

  private loadData(): void {
    this.setLoading(true);
    this.getTags();

    this.getCategories()
      .pipe(
        switchMap(() => this.getPublication()),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: ({ count, publications }: PublicationSearchResponse): void => {
          this.publications$.next(publications);
          this.count$.next(count);
          this.setLoading(false);
        },
        error: (error): void => {
          this.handingError(error);
          this.setLoading(false);
        },
      });
  }

  private getTags(): void {
    this.dataSource
      .getTags()
      .pipe(
        tap((tags: PublicationTagResponse[]) => this.tags$.next(tags)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private getCategories(): Observable<PublicationCategoryResponse[]> {
    return this.dataSource.getCategories().pipe(
      tap((categories: PublicationCategoryResponse[]) =>
        this.categories$.next(categories)
      ),
      takeUntil(this.ngUnsubscribe$)
    );
  }

  private getPublication(): Observable<PublicationSearchResponse> {
    return this.filters$.pipe(
      switchMap((filters: PublicationSearchFilter) =>
        this.dataSource.searchPublications({
          filter: filters,
          pagination: {
            skip: (filters.page - 1) * PageSize,
            take: PageSize,
          },
        })
      ),
      takeUntil(this.ngUnsubscribe$)
    );
  }

  private checkActiveFilters(): void {
    this.filters$
      .pipe(
        tap((filters: PublicationSearchFilter) => this.countFilters(filters)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private countFilters(filters: PublicationSearchFilter): void {
    let count = 0;

    Object.entries(filters).forEach(([key, value]: [string, unknown]): void => {
      if (value && key !== 'publicationCategoriesIds' && key !== 'page') {
        if (Array.isArray(value)) {
          count += value.length;
        } else {
          count += 1;
        }
      }
    });

    this.hasActiveFilters$.next(!!count);
  }

  private handingError(error: { [key: string]: unknown }): void {
    if (error.status === HttpResponseStatuses.BadRequest) {
      this.count$.next(0);
    }
  }

  private setLoading(value: boolean): void {
    this.isLoading$.next(value);
  }
}
